import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BalanceContext = createContext();

export const BalanceProvider = ({ children }) => {
  const [totalBalance, setTotalBalance] = useState(0);
  const [todayProfit, setTodayProfit] = useState(0.0000);
  const [totalCompletedProfit, setTotalCompletedProfit] = useState(0);
  const [isBalanceNegative, setIsBalanceNegative] = useState(false);
  const [dailyRatingCount, setDailyRatingCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPendingCount(0);
        return;
      }
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const res = await axios.get('/api/ratings/pending-count', config);
      setPendingCount(res.data.count);
    } catch (err) {
      console.error('Error fetching pending ratings count:', err);
      setPendingCount(0);
    }
  };

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token found. Cannot fetch balance.');
        setTotalBalance(0);
        setDailyRatingCount(0);
        return;
      }
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const res = await axios.get('/api/profile/balance', config);
      
      setTotalBalance(res.data.balance);
      setIsBalanceNegative(res.data.balance < 0);
      setTodayProfit(res.data.todayProfit);
      setDailyRatingCount(res.data.dailyRatingCount);
      fetchPendingCount();

    } catch (err) {
      console.error('Error fetching balance and profit:', err);
      setTotalBalance(0);
      setDailyRatingCount(0);
      if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
        console.error('Authentication token expired. Please re-authenticate.');
      }
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  const deductBalance = (amount) => {
    const newBalance = totalBalance - amount;
    setTotalBalance(newBalance);
    setIsBalanceNegative(newBalance < 0);
    return newBalance;
  };

  const addProfit = (amount) => {
    setTodayProfit(prev => prev + amount);
    setTotalBalance(prev => prev + amount);
  };

  const rechargeBalance = (amount) => {
    setTotalBalance(prev => prev + amount);
    setIsBalanceNegative(false);
  };

  return (
    <BalanceContext.Provider value={{
      totalBalance,
      todayProfit,
      totalCompletedProfit,
      isBalanceNegative,
      dailyRatingCount,
      pendingCount,
      deductBalance,
      addProfit,
      rechargeBalance,
      setTotalBalance,
      setTodayProfit,
      setTotalCompletedProfit,
      fetchBalance,
      fetchPendingCount
    }}>
      {children}
    </BalanceContext.Provider>
  );
};

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (!context) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
}; 